import React, { useMemo, useState } from "react";
import { PageHeader, Field, NumberInput, Result, FormulaBox, Note } from "@/components/CalcUI";
import { fmt } from "@/lib/utils";
import { useT } from "@/i18n/LanguageProvider";

const SEX_TBW = {
  male: 0.6,
  female: 0.5,
  geriatric_male: 0.5,
  geriatric_female: 0.45,
  child: 0.6,
  infant: 0.7,
};

function calcSodium({ weight, measuredNa, desiredNa, tbwFactor }) {
  if (!weight || !measuredNa || !desiredNa || weight <= 0) return null;
  const tbw = weight * tbwFactor;
  const naDeficit = tbw * (desiredNa - measuredNa);
  const isHyper = measuredNa > desiredNa;
  const freeWaterDeficit = isHyper ? tbw * (measuredNa / desiredNa - 1) : null;
  const safeChangeIn24h = 8;
  const totalChange = Math.abs(desiredNa - measuredNa);
  const recommendedHours = (totalChange / safeChangeIn24h) * 24;
  return { tbw, naDeficit, freeWaterDeficit, isHyper, recommendedHours, totalChange };
}

function calcPotassium({ weight, measuredK }) {
  if (!weight || !measuredK || weight <= 0 || measuredK <= 0) return null;
  const desired = 4.0;
  const deficitPerKg = Math.max(0, desired - measuredK) * 4;
  const deficit = deficitPerKg * weight;
  const hoursAt10 = deficit / 10;
  const hoursAt20 = deficit / 20;
  return { deficit, hoursAt10, hoursAt20 };
}

export default function Electrolytes() {
  const t = useT();
  const [weight, setWeight] = useState("");
  const [measuredNa, setMeasuredNa] = useState("");
  const [desiredNa, setDesiredNa] = useState("140");
  const [sex, setSex] = useState("male");
  const [measuredK, setMeasuredK] = useState("");

  const w = parseFloat(weight);
  const na = parseFloat(measuredNa);
  const nad = parseFloat(desiredNa);
  const k = parseFloat(measuredK);

  const sodium = useMemo(
    () => calcSodium({ weight: w, measuredNa: na, desiredNa: nad, tbwFactor: SEX_TBW[sex] }),
    [w, na, nad, sex]
  );
  const potassium = useMemo(() => calcPotassium({ weight: w, measuredK: k }), [w, k]);

  return (
    <div data-testid="page-electrolytes">
      <PageHeader title={t("elec.title")} subtitle={t("elec.subtitle")} formula={t("elec.formula")} />

      <div className="px-6 md:px-10 py-8 max-w-5xl space-y-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Field label={t("elec.weight")} unit="kg" testid="weight">
            <NumberInput value={weight} onChange={setWeight} placeholder={t("elec.weight.placeholder")} min={0} testid="weight-input" />
          </Field>
          <Field label={t("elec.tbw")} testid="sex">
            <select
              value={sex}
              onChange={(e) => setSex(e.target.value)}
              data-testid="sex-select"
              className="w-full bg-white border border-stone-200 rounded-sm px-3 py-2.5 text-base font-sans hover:border-stone-300 focus:border-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-700/30 transition-colors duration-100"
            >
              <option value="male">{t("elec.tbw.male")}</option>
              <option value="female">{t("elec.tbw.female")}</option>
              <option value="geriatric_male">{t("elec.tbw.geriatric_male")}</option>
              <option value="geriatric_female">{t("elec.tbw.geriatric_female")}</option>
              <option value="child">{t("elec.tbw.child")}</option>
              <option value="infant">{t("elec.tbw.infant")}</option>
            </select>
          </Field>
        </div>

        {/* SODIUM */}
        <section className="space-y-6" data-testid="section-sodium">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-sky-700 mb-1">
              {t("common.section")}
            </div>
            <h2 className="font-display text-2xl tracking-tight font-medium">{t("elec.sodium")}</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <Field label={t("elec.na.measured")} unit="mmol/L" testid="na-measured">
                <NumberInput value={measuredNa} onChange={setMeasuredNa} placeholder="e.g. 125" min={0} testid="na-measured-input" />
              </Field>
              <Field label={t("elec.na.desired")} unit="mmol/L" testid="na-desired">
                <NumberInput value={desiredNa} onChange={setDesiredNa} placeholder="140" min={0} testid="na-desired-input" />
              </Field>
              <Note kind="warning">{t("elec.na.note")}</Note>
            </div>

            <div className="space-y-4">
              <Result
                label={sodium?.isHyper ? t("elec.result.fwd") : t("elec.result.na_deficit")}
                value={
                  sodium
                    ? sodium.isHyper
                      ? fmt(sodium.freeWaterDeficit, 2)
                      : fmt(sodium.naDeficit, 0)
                    : "—"
                }
                unit={sodium?.isHyper ? "L" : "mEq"}
                big
                testid="result-na"
              />
              <Result
                label={t("elec.result.tbw")}
                value={sodium ? fmt(sodium.tbw, 1) : "—"}
                unit="L"
                testid="result-tbw"
              />
              <Result
                label={t("elec.result.correction_time")}
                value={sodium ? fmt(sodium.recommendedHours, 0) : "—"}
                unit="h"
                testid="result-correction-time"
              />
              <FormulaBox testid="sodium-breakdown">
                {sodium
                  ? sodium.isHyper
                    ? `TBW = ${fmt(w, 1)} × ${SEX_TBW[sex]} = ${fmt(sodium.tbw, 1)} L\nFWD = ${fmt(
                        sodium.tbw,
                        1
                      )} × (${fmt(na, 0)}/${fmt(nad, 0)} − 1) = ${fmt(sodium.freeWaterDeficit, 2)} L`
                    : `TBW = ${fmt(w, 1)} × ${SEX_TBW[sex]} = ${fmt(sodium.tbw, 1)} L\nNa = ${fmt(
                        sodium.tbw,
                        1
                      )} × (${fmt(nad, 0)} − ${fmt(na, 0)}) = ${fmt(sodium.naDeficit, 0)} mEq`
                  : t("elec.breakdown.empty")}
              </FormulaBox>
            </div>
          </div>
        </section>

        {/* POTASSIUM */}
        <section className="space-y-6" data-testid="section-potassium">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-sky-700 mb-1">
              {t("common.section")}
            </div>
            <h2 className="font-display text-2xl tracking-tight font-medium">{t("elec.potassium")}</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <Field label={t("elec.k.measured")} unit="mmol/L" testid="k-measured">
                <NumberInput value={measuredK} onChange={setMeasuredK} placeholder="e.g. 2.8" min={0} step="0.1" testid="k-measured-input" />
              </Field>
              <Note kind="warning">{t("elec.k.note")}</Note>
            </div>

            <div className="space-y-4">
              <Result
                label={t("elec.result.k_deficit")}
                value={potassium ? fmt(potassium.deficit, 0) : "—"}
                unit="mEq"
                big
                testid="result-k-deficit"
                accent={potassium && potassium.deficit > 0 ? "danger" : "default"}
              />
              <Result
                label={t("elec.result.k_peripheral")}
                value={potassium ? fmt(potassium.hoursAt10, 1) : "—"}
                unit="h"
                testid="result-k-peripheral"
              />
              <Result
                label={t("elec.result.k_central")}
                value={potassium ? fmt(potassium.hoursAt20, 1) : "—"}
                unit="h"
                testid="result-k-central"
              />
              <FormulaBox testid="potassium-breakdown">
                {potassium
                  ? `(4.0 − ${fmt(k, 1)}) × 4 mEq/kg × ${fmt(w, 1)} kg = ${fmt(potassium.deficit, 0)} mEq`
                  : t("elec.k.breakdown.empty")}
              </FormulaBox>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
